import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ShieldCheck, ShieldAlert, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function CertificatesPage() {
  const { authAxios } = useAuth();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const api = authAxios();
      const res = await api.get('/certificates');
      setCerts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Certificates fetch error:', err);
      toast.error('Failed to fetch certificates');
      setCerts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getStatusColor = (days) => {
    if (days < 3) return 'text-red-600 bg-red-600/10 border-red-600/20';
    if (days < 14) return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
    if (days < 30) return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
    return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <ShieldCheck className="w-8 h-8 text-[#EF694C] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
          Certificates
        </h1>
        <p className="text-sm text-[#a1a1aa] mt-1">
          {certs.length} certificates monitored
        </p>
      </div>

      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#a1a1aa]">Domain</TableHead>
                <TableHead className="text-[#a1a1aa]">Status</TableHead>
                <TableHead className="text-[#a1a1aa]">Expiry</TableHead>
                <TableHead className="text-[#a1a1aa]">Issuer</TableHead>
                <TableHead className="text-[#a1a1aa]">Subject</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {certs.map((cert) => (
                <TableRow key={cert.fqdn} className="border-white/5 hover:bg-white/[0.03]">
                  <TableCell className="font-mono text-white text-sm">
                    {cert.fqdn}
                  </TableCell>
                  <TableCell>
                    <Badge className={`border ${getStatusColor(cert.days_remaining)}`}>
                      {cert.days_remaining < 0 ? 'Expired' : `${cert.days_remaining} days`}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[#a1a1aa] text-sm">
                    {new Date(cert.not_after).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-[#a1a1aa] text-xs max-w-[200px] truncate" title={cert.issuer}>
                    {cert.issuer?.split(',')[0]}
                  </TableCell>
                  <TableCell className="text-[#a1a1aa] text-xs max-w-[200px] truncate" title={cert.subject}>
                    {cert.subject?.split(',')[0]}
                  </TableCell>
                </TableRow>
              ))}
              {certs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-[#52525b]">
                    No certificates found. Run a domain scan first.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
